import React, { useState, useMemo } from "react";
import {
  Header,
  Container,
  SpaceBetween,
  Box,
  Table,
  Pagination,
  TextFilter,
  Spinner,
  Select,
  SelectProps,
} from "@cloudscape-design/components";
import Plot from "react-plotly.js";
import {
  TimeGranularity,
  COLORS,
} from "./detailHelpers";

const TIME_LABEL: Record<TimeGranularity, string> = { day: "Day", week: "Week", month: "Month" };
import { PAGE_SIZE_OPTIONS } from "../constants";
import type { ProgramDetailData, RequestStatus, DetailPhicAlertRow, DetailPhicTrend } from "../../../redux/types/programTypes";

interface PhicSummaryRow {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  cmLocation: string;
  new: number;
  open: number;
  ack: number;
  closed: number;
}

export const PHICStatusDetail: React.FC<{ programs: string[]; timeGranularity: TimeGranularity; timeValue: string | null; selectedCMs?: string[]; selectedLocations?: string[]; detailData?: ProgramDetailData | null; detailStatus?: RequestStatus }> = ({ timeGranularity, timeValue, detailData = null, detailStatus = "idle" }) => {
  const [phicPage, setPhicPage] = useState(1);
  const [phicSearchText, setPhicSearchText] = useState("");
  const [expandedItems, setExpandedItems] = useState<ReadonlyArray<PhicSummaryRow>>([]);
  const [selectedPageSize, setSelectedPageSize] = useState<SelectProps.Option>(PAGE_SIZE_OPTIONS[0] as SelectProps.Option);
  const PHIC_PAGE_SIZE = parseInt(selectedPageSize.value || "10", 10);

  const currentTimeLabel = timeValue ? (timeGranularity === "week" ? `2026-WK${timeValue}` : `2026-${timeValue}`) : "";

  const timeRangeValue = timeValue || "";

  // Transform API rows: group by program + cm + cm_location, count statuses
  const tableData = useMemo(() => {
    if (!detailData?.table) return [];
    const apiRows = detailData.table as DetailPhicAlertRow[];
    const grouped: Record<string, PhicSummaryRow> = {};
    apiRows.forEach((row) => {
      const key = `${row.program}__${row.cm}__${row.cm_location}`;
      if (!grouped[key]) {
        grouped[key] = {
          program: row.program,
          parent_program: row.parent_program,
          cm: row.cm,
          cm_location: row.cm_location,
          cmLocation: `${row.cm}-${row.cm_location}`,
          new: 0, open: 0, ack: 0, closed: 0,
        };
      }
      const status = (row.status || "").toLowerCase();
      if (status === "new") grouped[key].new++;
      else if (status === "open") grouped[key].open++;
      else if (status === "acknowledged") grouped[key].ack++;
      else if (status === "closed") grouped[key].closed++;
    });
    return Object.values(grouped);
  }, [detailData]);

  // Build parent-child mapping using parent_program + cm + cm_location
  const { displayItems, childrenMap } = useMemo(() => {
    const cMap = new Map<string, PhicSummaryRow[]>();
    const existingParentKeys = new Set<string>();

    // Identify existing parents
    tableData.forEach((row) => {
      if (!row.parent_program) {
        existingParentKeys.add(`${row.program}__${row.cm}__${row.cm_location}`);
      }
    });

    // Gather children grouped by parent key
    tableData.forEach((row) => {
      if (row.parent_program) {
        const parentKey = `${row.parent_program}__${row.cm}__${row.cm_location}`;
        const existing = cMap.get(parentKey) || [];
        existing.push(row);
        cMap.set(parentKey, existing);
      }
    });

    // Build display items (top-level rows)
    const items: PhicSummaryRow[] = [];
    tableData.forEach((row) => {
      if (!row.parent_program) {
        items.push(row);
      }
    });

    // Create virtual parent rows for orphan children
    const createdVirtualParents = new Set<string>();
    tableData.forEach((row) => {
      if (row.parent_program) {
        const parentKey = `${row.parent_program}__${row.cm}__${row.cm_location}`;
        if (!existingParentKeys.has(parentKey) && !createdVirtualParents.has(parentKey)) {
          createdVirtualParents.add(parentKey);
          const virtualParent: PhicSummaryRow = {
            program: row.parent_program,
            parent_program: null,
            cm: row.cm,
            cm_location: row.cm_location,
            cmLocation: `${row.cm}-${row.cm_location}`,
            new: 0, open: 0, ack: 0, closed: 0,
          };
          items.push(virtualParent);
        }
      }
    });

    return { displayItems: items, childrenMap: cMap };
  }, [tableData]);

  // Filtered display data
  const filteredDisplay = useMemo(() => {
    if (!phicSearchText.trim()) return displayItems;
    const q = phicSearchText.toLowerCase();
    return displayItems.filter((r) => {
      const rowKey = `${r.program}__${r.cm}__${r.cm_location}`;
      return (
        r.program.toLowerCase().includes(q) ||
        r.cmLocation.toLowerCase().includes(q) ||
        // Include parent if any children match
        (childrenMap.get(rowKey) || []).some((child) =>
          child.program.toLowerCase().includes(q) ||
          child.cmLocation.toLowerCase().includes(q)
        )
      );
    });
  }, [displayItems, childrenMap, phicSearchText]);

  const phicPageItems = filteredDisplay.slice((phicPage - 1) * PHIC_PAGE_SIZE, phicPage * PHIC_PAGE_SIZE);

  const apiTrend = detailData?.trend as DetailPhicTrend | undefined;

  // Memoize chart data so table interactions (expand/pagination/search) don't cause chart re-renders
  const chartData = useMemo(() => {
    if (!apiTrend || !apiTrend.series) return [];
    return [
      ...apiTrend.series.map((s, idx) => ({
        x: apiTrend.dates,
        y: s.data,
        name: s.cause_type,
        type: "bar" as const,
        marker: { color: COLORS[idx % COLORS.length] },
        hovertemplate: "%{fullData.name}<br>Value: %{y}<br>Time: %{x}<extra></extra>",
      })),
      {
        x: apiTrend.dates,
        y: apiTrend.totals,
        name: "Total",
        type: "scatter" as const,
        mode: "lines+markers" as const,
        marker: { color: "#FF6361", size: 6 },
        line: { width: 2.5, color: "#FF6361" },
        hovertemplate: "%{fullData.name}<br>Value: %{y}<br>Time: %{x}<extra></extra>",
      },
    ];
  }, [apiTrend]);

  const chartLayout = useMemo(() => ({
    height: 320, margin: { t: 10, b: 80, l: 50, r: 50 },
    barmode: "stack" as const,
    yaxis: { title: "Count", rangemode: "nonnegative" as const, showgrid: true, gridcolor: "rgba(0,0,0,0.1)", dtick: undefined },
    xaxis: { title: timeGranularity === "week" ? "Week" : "Month", tickangle: -45, type: "category" as const, showgrid: false },
    legend: { orientation: "h" as const, y: -0.3, font: { size: 9 } },
    font: { size: 10 },
    plot_bgcolor: "rgba(0,0,0,0)",
  }), [timeGranularity]);

  const chartConfig = useMemo(() => ({ displayModeBar: false, responsive: true }), []);

  if (detailStatus === "loading") {
    return (
      <Container>
        <Box textAlign="center" padding={{ vertical: "xl" }}>
          <SpaceBetween size="s" alignItems="center">
            <Spinner size="large" />
            <Box color="text-body-secondary">Loading detail data...</Box>
          </SpaceBetween>
        </Box>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      <div data-testid="program-detail-phic-summary">
      <Container header={<Header variant="h2"><span data-testid="program-detail-phic-summary-title">{`PHIC Status Summary — by ${TIME_LABEL[timeGranularity]}`}</span></Header>}>
        <SpaceBetween size="s">
          <div className="phic-detail-legend" data-testid="program-phic-legend">
            <span><span className="phic-legend-new">New:</span> New alarm today</span>
            <span><span className="phic-legend-open">Open:</span> Not yet responded</span>
            <span><span className="phic-legend-ack">Ack:</span> Under investigation</span>
            <span><span className="phic-legend-closed">Closed:</span> Resolved</span>
          </div>
          <div data-testid="program-table-phic-summary">
          <Table<PhicSummaryRow>
            trackBy={(item: PhicSummaryRow) => `${item.program}__${item.cm}__${item.cm_location}`}
            columnDefinitions={[
              { id: "program", header: "Program", width: 150, cell: (item: PhicSummaryRow) => {
                const rowKey = `${item.program}__${item.cm}__${item.cm_location}`;
                const childCount = (childrenMap.get(rowKey) || []).length;
                if (!item.parent_program && childCount > 0) {
                  return (
                    <span className="detail-expandable-program">
                      <span>{item.program}</span>
                      <span className="detail-child-count-badge">{childCount}</span>
                    </span>
                  );
                }
                return item.program;
              }},
              { id: "cmLocation", header: "CM & Location", width: 100, cell: (item: PhicSummaryRow) => item.parent_program ? "" : item.cmLocation },
              { id: "timeRange", header: "Time Range", width: 120, cell: () => timeRangeValue },
              { id: "new", header: "New", cell: (item: PhicSummaryRow) => item.new },
              { id: "open", header: "Open", cell: (item: PhicSummaryRow) => item.open },
              { id: "ack", header: "Ack", cell: (item: PhicSummaryRow) => item.ack },
              { id: "closed", header: "Closed", cell: (item: PhicSummaryRow) => item.closed },
            ]}
            items={phicPageItems}
            expandableRows={{
              getItemChildren: (item: PhicSummaryRow) => {
                const rowKey = `${item.program}__${item.cm}__${item.cm_location}`;
                return childrenMap.get(rowKey) || [];
              },
              isItemExpandable: (item: PhicSummaryRow) => {
                const rowKey = `${item.program}__${item.cm}__${item.cm_location}`;
                return (childrenMap.get(rowKey) || []).length > 0;
              },
              expandedItems: expandedItems,
              onExpandableItemToggle: ({ detail }) => {
                const item = detail.item;
                const isExpanded = detail.expanded;
                const getKey = (i: PhicSummaryRow) => `${i.program}__${i.cm}__${i.cm_location}`;
                if (isExpanded) {
                  setExpandedItems((prev) => [...prev, item]);
                } else {
                  setExpandedItems((prev) => prev.filter((i) => getKey(i) !== getKey(item)));
                }
              },
            }}
            variant="embedded"
            filter={<TextFilter filteringText={phicSearchText} onChange={({ detail }) => { setPhicSearchText(detail.filteringText); setPhicPage(1); }} filteringPlaceholder="Search by program, CM or CM location..." />}
            empty={<Box textAlign="center" color="inherit" padding={{ vertical: "l" }}><b>No PHIC status data</b><Box color="text-body-secondary" padding={{ top: "s" }}>No PHIC alert records available for the selected period.</Box></Box>}
            pagination={
              <div className="detail-table-toolbar">
                <div className="detail-page-size-selector">
                  <span className="detail-page-size-label">Page size:</span>
                  <div className="detail-page-size-select">
                    <Select
                      selectedOption={selectedPageSize}
                      onChange={({ detail }) => { setSelectedPageSize(detail.selectedOption as SelectProps.Option); setPhicPage(1); }}
                      options={PAGE_SIZE_OPTIONS}
                    />
                  </div>
                </div>
                {filteredDisplay.length > PHIC_PAGE_SIZE && (
                  <div data-testid="program-table-phic-summary-pagination">
                    <Pagination currentPageIndex={phicPage} pagesCount={Math.ceil(filteredDisplay.length / PHIC_PAGE_SIZE)} onChange={({ detail }) => setPhicPage(detail.currentPageIndex)} />
                  </div>
                )}
              </div>
            }
          />
          </div>
        </SpaceBetween>
      </Container>
      </div>

      {/* Sev1 Alarm Cause Pareto  Trend */}
      <div data-testid="program-chart-pareto">
      <Container header={<Header variant="h2"><span data-testid="program-chart-pareto-title">{`Sev1 Alarm Cause Pareto Trend — by ${TIME_LABEL[timeGranularity]}`}</span></Header>}>
        <Plot
          data={chartData}
          layout={chartLayout}
          config={chartConfig}
          style={{ width: "100%" }}
        />
      </Container>
      </div>
    </SpaceBetween>
  );
};
